import "dayjs/locale/ja";

import dayjs, { ConfigType, Dayjs, ManipulateType } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.locale("ja");
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(relativeTime);

dayjs.tz.setDefault("Asia/Tokyo");

export type DateType = ConfigType;

export type { Dayjs, ManipulateType } from "dayjs";

/** input 要素の value などで使う標準フォーマット定数 */
export const FORMAT = {
  DATE: "YYYY-MM-DD",
  DATETIME_LOCAL: "YYYY-MM-DDTHH:mm",
  DATETIME_LOCAL_SECONDS: "YYYY-MM-DDTHH:mm:ss",
} as const;

/**
 * オフセットやタイムゾーン指定を持たない ISO 風の文字列。
 * Asia/Tokyo の壁時計として解釈する対象で、GMT などの指定付き文字列は含めない。
 */
const NAIVE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/;

/**
 * ハイフンなしのコンパクト日付(ISO 8601 basic format)。
 * PHR の effectiveTime や MynaAuthManager の linkage 日付が YYYYMMDD 形式で流れてくる。
 * dayjs は素のパースだとこの形式を端末ローカルの壁時計として扱うため、明示的に判別する。
 */
const COMPACT_DATE_PATTERN = /^\d{6}(\d{2})?$/;

const COMPACT_DATE_FORMAT_BY_LENGTH: Record<number, string> = {
  6: "YYYYMM",
  8: "YYYYMMDD",
};

/** format 自体にオフセットトークンが含まれるかどうか */
const hasOffsetToken = (format: string) => /Z/.test(format);

/**
 * 入力を Asia/Tokyo の Dayjs へ変換する。
 * - 引数省略時は現在時刻を返す
 * - オフセットなしの ISO 風文字列(YYYY-MM-DD、YYYY-MM-DD HH:mm 等)、
 *   コンパクト日付(YYYYMMDD、YYYYMM)、format 指定時は Asia/Tokyo の壁時計として解釈する
 *   (format 自体にオフセットトークンを含む場合を除く)
 * - オフセット付き文字列・Date・Dayjs・timestamp は瞬間を保ったまま Asia/Tokyo へ変換する
 * - format 指定時は customParseFormat でパースし、strict で厳密一致を指定できる
 */
export const tzDate = (date: DateType = Date.now(), format?: string, strict?: boolean) => {
  if (format) {
    const parsed = dayjs(date, format, strict);

    if (!parsed.isValid() || hasOffsetToken(format)) {
      return parsed.tz();
    }

    // ローカル TZ でパースされた壁時計を Asia/Tokyo の壁時計として解釈し直す
    return dayjs.tz(parsed.format("YYYY-MM-DDTHH:mm:ss.SSS"));
  }

  if (typeof date === "string" && NAIVE_DATE_PATTERN.test(date)) {
    // パターンに一致した文字列は Date のロールオーバーにより必ず valid になるため、
    // invalid 入力で例外を投げうる dayjs.tz にそのまま渡してよい
    return dayjs.tz(date);
  }

  if (typeof date === "string" && COMPACT_DATE_PATTERN.test(date)) {
    // 旧 moment は ISO 8601 basic format を Asia/Tokyo の壁時計として解釈していた。
    // 実在しない日付(20230229 等)は moment と同じく invalid にするため strict でパースする
    const parsed = dayjs(date, COMPACT_DATE_FORMAT_BY_LENGTH[date.length], true);

    if (!parsed.isValid()) return parsed.tz();

    return dayjs.tz(parsed.format("YYYY-MM-DDTHH:mm:ss.SSS"));
  }

  return dayjs(date).tz();
};

/**
 * start から step * unit 刻みで進み、end 以前(end ちょうどを含む)の日時を配列で返す。
 */
export const tzRange = (start: DateType, end: DateType, unit: ManipulateType, step = 1): Dayjs[] => {
  if (step <= 0) {
    return [];
  }

  const result: Dayjs[] = [];
  const last = tzDate(end);
  let current = tzDate(start);

  while (current.isSameOrBefore(last)) {
    result.push(current);
    current = current.add(step, unit);
  }

  return result;
};
